import { useState } from "react";
import { Calculator as CalcIcon, PencilLine } from "lucide-react";
import Whiteboard from "./Whiteboard";
import Calculator from "./Calculator";
import Folder from "./Folder";

/**
 * Floating "study tools" launcher used inside game pages. Click the folder
 * and the Whiteboard / Calculator pop out as papers; clicking a paper opens
 * the tool (the folder closes itself via the same click bubbling up).
 *
 * Positioned bottom-right to stay out of the way of the answer area.
 */
export default function MatchTools({ position = "right", questionKey }) {
  const [openWb, setOpenWb] = useState(false);
  const [openCalc, setOpenCalc] = useState(false);

  // Bumped well above the bottom edge so the folder never collides with the
  // "Made with Emergent" badge that lives at bottom: 16px / right: 16px on
  // the preview deployment. Without this push, the badge sits on top of the
  // launcher and silently swallows every click.
  const side = position === "left" ? "left-4 sm:left-6" : "right-4 sm:right-6";
  return (
    <>
      <div
        className={`fixed bottom-24 sm:bottom-28 ${side} z-[70] match-tools-folder`}
        data-testid="match-tools"
      >
        <div data-testid="match-tools-trigger">
          {/* size 0.55 ≈ the 48px gear/FAB footprint; origin keeps the scaled
              folder hugging the bottom-right corner of its layout box */}
          <Folder
            color="#6366F1"
            size={0.55}
            className="origin-bottom-right"
            items={[
              <ToolPaper
                key="wb"
                label="whiteboard"
                icon={<PencilLine size={30} />}
                onClick={() => setOpenWb(true)}
                testid="match-tools-open-whiteboard"
              />,
              <ToolPaper
                key="calc"
                label="calculator"
                icon={<CalcIcon size={30} />}
                onClick={() => setOpenCalc(true)}
                testid="match-tools-open-calculator"
              />,
            ]}
          />
        </div>
      </div>

      <Whiteboard open={openWb}   onClose={() => setOpenWb(false)} resetKey={questionKey} />
      <Calculator open={openCalc} onClose={() => setOpenCalc(false)} />
    </>
  );
}

/**
 * Fills a folder paper with a clickable tool face (icon only — text would be
 * illegible at the folder's small scale). The click intentionally bubbles up
 * to the folder so it closes while the tool modal opens.
 */
function ToolPaper({ label, icon, onClick, testid }) {
  return (
    <div
      onClick={onClick}
      data-testid={testid}
      role="button"
      aria-label={`Open ${label}`}
      className="w-full h-full flex items-center justify-center cursor-pointer select-none"
      style={{ color: "#312E81" }}
    >
      {icon}
    </div>
  );
}
